"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import dynamicImport from "next/dynamic";
import { useTranslations } from "next-intl";
import remarkGfm from "remark-gfm";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { alpha } from "@mui/material/styles";
import { IconArrowLeft, IconCheck, IconRestart } from "@/components/icons";
import { colors } from "@/theme/colors";
import { fonts, shadows } from "@/theme/tokens";
import { isValidEmail } from "@/lib/validation";
import { Link } from "@/i18n/navigation";
import { PRICE_SHEET_HREF } from "@/lib/brand";
import type { Brief } from "@/lib/onboarding/brief";
import { GATHERED_STEPS, parseMeta, stripMeta } from "@/lib/onboarding/meta";
import { Wordmark } from "@/components/common/Wordmark";
import { AuroraBackdrop } from "./AuroraBackdrop";
import { PresenceOrb } from "./PresenceOrb";
import { type Prefill, useAssistantChat } from "./useAssistantChat";
// Surface styling lives in assistantSurface.ts — recolor/reshape there.
import {
  INK,
  TXT,
  TXT_DIM,
  GLASS,
  GLASS_STRONG,
  BORDER,
  monoLabel,
  quietActionSx,
  fieldSx as darkFieldSx,
  chipSx,
  entranceSx,
  markdownSx,
} from "./assistantSurface";

/** First-turn quick-reply seeds, keyed to `start.seeds.*`. */
const SEED_KEYS = ["website", "ios", "custom", "unsure"] as const;

// The unified/remark pipeline is heavy; download it only once the first
// assistant reply needs formatting, not on /start's first paint.
const Markdown = dynamicImport(() => import("react-markdown"), { ssr: false });

/** A crisp stroked paper-plane send glyph, optically centered in its button. */
function SendGlyph() {
  return (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        width: 20,
        height: 20,
        display: "block",
        transform: "translate(-1px, 1px)",
      }}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22 11 13 2 9 22 2Z" />
    </Box>
  );
}

/** Tappable suggested replies — rendered both in the hero and the chat dock. */
function SuggestionChips({
  items,
  onSelect,
}: {
  items: string[];
  onSelect: (value: string) => void;
}) {
  return (
    <>
      {items.map((label) => (
        <Chip
          key={label}
          label={label}
          variant="outlined"
          onClick={() => onSelect(label)}
          sx={chipSx}
        />
      ))}
    </>
  );
}

/** A chat row: assistant (orb + frosted bubble, left) or visitor (ink block,
 *  right). Memoized so settled messages skip re-parsing their Markdown on
 *  every streamed token of the newest reply. */
const Bubble = memo(function Bubble({
  role,
  text,
  caret,
}: {
  role: "user" | "assistant";
  text: string;
  caret?: boolean;
}) {
  if (role === "user") {
    return (
      <Box
        sx={{
          ...entranceSx,
          alignSelf: "flex-end",
          maxWidth: "85%",
          px: 2,
          py: 1.25,
          borderRadius: "4px 4px 2px 4px",
          backgroundColor: colors.textPrimary,
          color: colors.paper,
          whiteSpace: "pre-wrap",
          lineHeight: 1.55,
        }}
      >
        {text}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        ...entranceSx,
        display: "flex",
        gap: 1.25,
        alignItems: "flex-start",
        maxWidth: "94%",
      }}
    >
      <Box sx={{ mt: 0.25 }}>
        <PresenceOrb size={28} active={caret} />
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: "2px 4px 4px 4px",
          backgroundColor: GLASS,
          backdropFilter: "blur(12px)",
          border: "1px solid",
          borderColor: BORDER,
          color: TXT,
          lineHeight: 1.55,
          ...(caret ? { whiteSpace: "pre-wrap" } : markdownSx),
        }}
      >
        {/* Plain text while streaming; format once the turn settles. */}
        {caret ? text : <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>}
        {caret ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "inline-block",
              width: "2px",
              height: "1.05em",
              ml: "1px",
              verticalAlign: "text-bottom",
              backgroundColor: colors.primary,
              animation: "csCaret 1s steps(2) infinite",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
});

/** A single timeline node — ringed dot that fills green when its step is done. */
function StepDot({ done, active }: { done: boolean; active: boolean }) {
  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        width: 24,
        height: 24,
        borderRadius: "50%",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        border: "2px solid",
        borderColor: done
          ? colors.success
          : active
            ? colors.primary
            : alpha(colors.textPrimary, 0.3),
        // Solid fill so the timeline track doesn't show through the ring.
        backgroundColor: done ? colors.success : INK,
        boxShadow: active ? `0 0 14px ${colors.primary}aa` : "none",
        transition: "all 0.3s ease",
      }}
    >
      {done ? (
        <IconCheck size={13} style={{ color: colors.paper }} />
      ) : (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: active ? colors.primary : TXT_DIM,
          }}
        />
      )}
    </Box>
  );
}

/**
 * Progress rail: the interview steps as a glowing timeline. On desktop it hugs
 * the left edge and spreads the full height, with a gradient track that fills as
 * steps complete. On mobile it's a scrollable horizontal stepper.
 */
function ProgressRail({
  gathered,
  activeStep,
}: {
  gathered: Set<string>;
  activeStep: string | null;
}) {
  const t = useTranslations("start");
  const n = GATHERED_STEPS.length;
  const activeIndex = activeStep
    ? (GATHERED_STEPS as readonly string[]).indexOf(activeStep)
    : n - 1;
  // The track fills from the top down to the current step (covering done steps).
  const fillPct = activeStep ? (activeIndex / (n - 1)) * 100 : 100;

  return (
    <Box
      component="nav"
      aria-label={t("progressLabel")}
      sx={{ flexShrink: 0, width: { md: 200 } }}
    >
      {/* Desktop: vertical timeline (top-aligned, comfortably spaced). */}
      <Box sx={{ display: { xs: "none", md: "block" }, pt: 8 }}>
        <Typography sx={{ ...monoLabel, color: TXT_DIM, mb: 5 }}>
          {t("progressLabel")}
        </Typography>
        <Box sx={{ position: "relative" }}>
          {/* Track + gradient fill, centered on the 24px dots. */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              left: 11,
              top: 12,
              bottom: 12,
              width: "2px",
              backgroundColor: alpha(colors.textPrimary, 0.14),
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              left: 11,
              top: 12,
              height: `calc((100% - 24px) * ${fillPct / 100})`,
              width: "2px",
              backgroundColor: colors.primary,
              transition: "height 0.4s ease",
            }}
          />
          <Box
            component="ol"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              // Scales with window height, bounded so it never cramps or over-stretches.
              gap: "clamp(2.5rem, 8vh, 6.5rem)",
            }}
          >
            {GATHERED_STEPS.map((step) => {
              const done = gathered.has(step);
              const active = step === activeStep;
              return (
                <Box
                  component="li"
                  key={step}
                  aria-current={active ? "step" : undefined}
                  sx={{ display: "flex", alignItems: "center", gap: 1.75 }}
                >
                  <StepDot done={done} active={active} />
                  <Typography
                    sx={{
                      ...monoLabel,
                      whiteSpace: "nowrap",
                      fontWeight: active ? 700 : 500,
                      color: done || active ? TXT : TXT_DIM,
                    }}
                  >
                    {t(`progress.${step}`)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Mobile: horizontal scrollable stepper. */}
      <Box
        className="no-scrollbar"
        sx={{
          display: { xs: "flex", md: "none" },
          gap: 2.5,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {GATHERED_STEPS.map((step) => {
          const done = gathered.has(step);
          const active = step === activeStep;
          return (
            <Box
              key={step}
              aria-current={active ? "step" : undefined}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0,
              }}
            >
              <StepDot done={done} active={active} />
              <Typography
                sx={{
                  ...monoLabel,
                  whiteSpace: "nowrap",
                  color: done || active ? TXT : TXT_DIM,
                }}
              >
                {t(`progress.${step}`)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** The brief the studio receives, shown as a glass card on completion. */
function SummaryCard({ brief }: { brief: Brief }) {
  const t = useTranslations("start");
  const tp = useTranslations("pricing");
  const pkg =
    brief.packageInterest === "unsure"
      ? t("packageUnsure")
      : tp(`tiers.${brief.packageInterest}.name`);

  const rows = (
    [
      [t("summary.package"), pkg],
      [t("summary.business"), brief.businessSummary],
      [t("summary.goal"), brief.projectGoal],
      [t("summary.timeline"), brief.timeline],
      [t("summary.care"), t(`careLabels.${brief.careInterest}`)],
      [t("summary.name"), brief.contactName],
      [t("summary.email"), brief.email],
      [t("summary.phone"), brief.phone],
    ] as Array<[string, string]>
  ).filter(([, value]) => value.trim().length > 0);

  return (
    <Box
      sx={{
        textAlign: "left",
        p: { xs: 2.5, md: 3 },
        borderRadius: "20px",
        backgroundColor: colors.paper,
        border: "1px solid",
        borderColor: BORDER,
        boxShadow: shadows.card,
      }}
    >
      <Typography sx={{ ...monoLabel, color: colors.primary, mb: 1.5 }}>
        {t("summary.title")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          rowGap: 0.85,
          columnGap: 2.5,
        }}
      >
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: "contents" }}>
            <Typography sx={{ color: TXT_DIM, fontSize: "0.85rem" }}>
              {label}
            </Typography>
            <Typography sx={{ color: TXT, fontSize: "0.9rem" }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Outage-only fallback: capture an email locally so the visitor isn't stuck. */
function EmailFallback() {
  const t = useTranslations("start.fallback");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (sent) return <Alert severity="success">{t("success")}</Alert>;
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValidEmail(email)) return setError(t("invalidEmail"));
        setSent(true);
      }}
      sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
    >
      <TextField
        type="email"
        label={t("emailLabel")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={Boolean(error)}
        helperText={error ?? " "}
        autoComplete="email"
        sx={{ ...darkFieldSx, flex: 1, minWidth: 220 }}
      />
      <Button
        type="submit"
        variant="contained"
        sx={{ height: 56, px: 3.5, flexShrink: 0 }}
      >
        {t("submit")}
      </Button>
    </Box>
  );
}

/**
 * Full-screen "assistant mode": a cool paper canvas washed with drifting light,
 * a living presence orb, a filling progress rail, streamed Markdown replies, a
 * bottom-docked frosted input, and a summary card on completion. Resumes from
 * localStorage; degrades to an email capture on a backend outage. Mobile-first.
 */
export function OnboardingAssistant({ prefill }: { prefill: Prefill }) {
  const t = useTranslations("start");
  const { messages, status, brief, send, stop, reset } =
    useAssistantChat(prefill);
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, status]);

  const isStreaming = status === "streaming";
  const isDone = status === "done";
  const isEmpty = messages.length === 0;
  const last = messages.at(-1);
  const lastAssistant = last?.role === "assistant" ? last.content : undefined;

  const meta = useMemo(() => parseMeta(lastAssistant ?? ""), [lastAssistant]);
  const gathered = useMemo(
    () => new Set<string>(isDone ? GATHERED_STEPS : meta.gathered),
    [isDone, meta.gathered],
  );
  const activeStep = useMemo<string | null>(
    () =>
      isDone ? null : (GATHERED_STEPS.find((s) => !gathered.has(s)) ?? null),
    [isDone, gathered],
  );

  const showInput = !isDone && status !== "unavailable";
  const suggestions = isEmpty
    ? SEED_KEYS.map((k) => t(`seeds.${k}`))
    : status === "idle"
      ? meta.suggestions
      : [];

  const canSend = !isStreaming && draft.trim().length > 0;

  const submitDraft = () => {
    send(draft);
    setDraft("");
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: INK,
        color: TXT,
        overflow: "hidden",
        // Global keyframes referenced by descendants.
        "@keyframes msgIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes csCaret": { "50%": { opacity: 0 } },
        "@keyframes stopPulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 1 },
          "50%": { transform: "scale(0.62)", opacity: 0.55 },
        },
      }}
    >
      <AuroraBackdrop />

      {/* Top bar */}
      <Box
        component="header"
        sx={{
          position: "relative",
          zIndex: 2,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2.5, md: 4 },
          py: 2,
        }}
      >
        <MuiLink component={Link} href="/" underline="none">
          <Wordmark fontSize="1.05rem" />
        </MuiLink>
        <MuiLink
          component={Link}
          href="/"
          underline="none"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            ...quietActionSx,
          }}
        >
          <IconArrowLeft size={14} />
          {t("back")}
        </MuiLink>
      </Box>

      {/* Body: rail + conversation */}
      <Box
        component="main"
        id="main-content"
        sx={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 7 },
          width: "100%",
          // Full-bleed and left-weighted: the rail hugs the left edge; the
          // conversation sits beside it rather than centered in the viewport.
          px: { xs: 2.5, md: 6 },
          pb: { xs: 2.5, md: 4 },
        }}
      >
        <ProgressRail gathered={gathered} activeStep={activeStep} />

        {/* Conversation column — centered in the viewport. A right-side spacer
            equal to the rail width (below) balances the rail so `mx: auto`
            centers the chat on screen, not just in the space beside the rail. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            maxWidth: 820,
            width: "100%",
            mx: "auto",
          }}
        >
          {/* Hero — persistent presence above the conversation. */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 2,
              pt: { xs: 3, md: 6 },
              pb: 1,
            }}
          >
            <PresenceOrb size={84} active={isStreaming} idle />
            <Typography
              variant="h1"
              sx={{
                fontFamily: fonts.heading,
                fontWeight: 800,
                fontSize: "clamp(1.5rem, 3.5vw, 2.4rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                maxWidth: 540,
              }}
            >
              {t("greeting")}
            </Typography>
          </Box>

          {/* The conversation happens here — between the hero and the input. */}
          <Box
            role="log"
            aria-live="polite"
            aria-label={t("assistantName")}
            className="no-scrollbar"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              py: 2,
            }}
          >
            {messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              return (
                <Bubble
                  key={i}
                  role={m.role}
                  text={
                    m.role === "assistant" ? stripMeta(m.content) : m.content
                  }
                  caret={isStreaming && isLast && m.role === "assistant"}
                />
              );
            })}

            {isDone && brief ? (
              <Box sx={{ mt: 1 }}>
                <SummaryCard brief={brief} />
                <Box sx={{ textAlign: "center", mt: 2.5 }}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5 }}
                  >
                    {t("done.title")}
                  </Typography>
                  <Typography
                    sx={{ color: TXT_DIM, mb: 1.5, fontSize: "0.95rem" }}
                  >
                    {t("done.body")}
                  </Typography>
                  <Box
                    component="a"
                    href={PRICE_SHEET_HREF}
                    sx={{
                      ...monoLabel,
                      color: colors.primary,
                      textDecoration: "none",
                    }}
                  >
                    {t("done.sheet")}
                  </Box>
                </Box>
              </Box>
            ) : null}
            <div ref={endRef} />
          </Box>

          {/* Dock: errors, suggestions, input — or the outage fallback. */}
          <Box sx={{ flexShrink: 0, pt: 1.5 }}>
            {status === "unavailable" ? (
              <EmailFallback />
            ) : showInput ? (
              <>
                {status === "busy" || status === "error" ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {t(status === "busy" ? "errors.busy" : "errors.generic")}
                  </Alert>
                ) : null}

                {/* Suggested replies — seeds when empty, contextual mid-chat. */}
                {suggestions.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}
                  >
                    <SuggestionChips items={suggestions} onSelect={send} />
                  </Box>
                ) : null}

                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitDraft();
                  }}
                  sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}
                >
                  <TextField
                    fullWidth
                    multiline
                    maxRows={5}
                    placeholder={t("inputPlaceholder")}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        submitDraft();
                      }
                    }}
                    disabled={isStreaming}
                    sx={darkFieldSx}
                  />
                  <Button
                    type={isStreaming ? "button" : "submit"}
                    onClick={isStreaming ? stop : undefined}
                    disabled={!isStreaming && !canSend}
                    variant="contained"
                    aria-label={isStreaming ? t("stop") : t("send")}
                    sx={{
                      minWidth: 0,
                      width: 52,
                      height: 52,
                      borderRadius: "4px",
                      p: 0,
                      flexShrink: 0,
                      // Paper glyph on the active/streaming fills; muted on the idle fill.
                      color: canSend || isStreaming ? colors.paper : TXT_DIM,
                      transition: "background 0.2s ease",
                      // Streaming → violet; ready → ink; idle → tinted fill.
                      background: isStreaming
                        ? colors.primary
                        : canSend
                          ? colors.textPrimary
                          : GLASS_STRONG,
                      "&:hover": {
                        background: isStreaming || canSend
                          ? colors.primary
                          : GLASS_STRONG,
                      },
                      // Visible-but-inactive empty state (not the default MUI grey).
                      "&.Mui-disabled": {
                        background: GLASS_STRONG,
                        color: TXT_DIM,
                      },
                    }}
                  >
                    {isStreaming ? (
                      <Box
                        aria-hidden
                        sx={{
                          width: 13,
                          height: 13,
                          borderRadius: "3px",
                          backgroundColor: colors.paper,
                          animation: "stopPulse 1.1s ease-in-out infinite",
                          "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                          },
                        }}
                      />
                    ) : (
                      <SendGlyph />
                    )}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Button
                    variant="text"
                    disabled={isStreaming}
                    onClick={() => send(t("humanAction"))}
                    sx={quietActionSx}
                  >
                    {t("human")}
                  </Button>
                  {!isEmpty ? (
                    <Button
                      variant="text"
                      onClick={reset}
                      startIcon={<IconRestart size={15} />}
                      sx={quietActionSx}
                    >
                      {t("startOver")}
                    </Button>
                  ) : null}
                </Box>
              </>
            ) : (
              // Done — offer a fresh start.
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={reset}
                  startIcon={<IconRestart size={15} />}
                  sx={quietActionSx}
                >
                  {t("startOver")}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        {/* Right spacer — mirrors the rail width so the chat centers on screen. */}
        <Box
          aria-hidden
          sx={{
            display: { xs: "none", md: "block" },
            width: 200,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
}
